/**
 * Public docs page — renders long-form Markdown documents stored under
 * /app/docs/ behind whitelisted slugs (e.g. /docs/parent-teen-charter,
 * /docs/schools).
 *
 * - Wraps content in the same brand chrome as the homepage (logo, nav, footer)
 *   so visitors don't feel they've left the marketing site.
 * - Uses react-markdown + remark-gfm so the same Markdown that lives in the
 *   GitHub repo renders cleanly on the public site (no duplication).
 * - Auto-builds a sticky table of contents from H2 headings.
 * - SEO-friendly: title and description come from the API response and feed
 *   into PageMeta, with a per-doc canonical URL.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SiteNav from '../components/SiteNav';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, BookOpen, Loader2 } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Slim brand chrome (logo + nav) ────────────────────────────────────────

const DocsFooter = () => (
  <footer className="border-t border-gray-100 bg-white mt-16">
    <div className="max-w-6xl mx-auto px-4 py-10 text-sm text-gray-500 flex flex-wrap items-center justify-between gap-3">
      <p>© {new Date().getFullYear()} HiBFF · Brave Friends Forever.</p>
      <div className="flex gap-4">
        <Link to="/trust" className="hover:text-gray-900" data-testid="docs-footer-trust">Trust</Link>
        <Link to="/contact" className="hover:text-gray-900" data-testid="docs-footer-contact">Contact</Link>
        <a href="mailto:safeguarding@hibff.com" className="hover:text-gray-900">Safeguarding</a>
      </div>
    </div>
  </footer>
);

// ── Helpers ───────────────────────────────────────────────────────────────

const slugify = (s) =>
  String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const headingToId = (children) => {
  const text = Array.isArray(children)
    ? children.map((c) => (typeof c === 'string' ? c : '')).join('')
    : String(children || '');
  return slugify(text);
};

// Build the H2 table of contents from the Markdown body
const extractToc = (md) => {
  if (!md) return [];
  const out = [];
  for (const line of md.split('\n')) {
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      const text = m[1].replace(/[`*_]/g, '').trim();
      out.push({ text, id: slugify(text) });
    }
  }
  return out;
};

// ── Page component ────────────────────────────────────────────────────────

// ── ReactMarkdown component map (module-scoped so each DocsPage render
//    doesn't rebuild the whole component tree — otherwise every re-render
//    remounts every heading, paragraph, list item… defeats React's
//    reconciliation). Fixes visible flicker when the doc loads in.
const MARKDOWN_COMPONENTS = {
  h1: ({ children }) => <h1 id={headingToId(children)} className="text-4xl font-bold text-gray-900 mb-4 mt-2" style={{ fontFamily: 'Georgia, serif' }}>{children}</h1>,
  h2: ({ children }) => <h2 id={headingToId(children)} className="text-2xl font-bold text-gray-900 mt-12 mb-3 scroll-mt-28" style={{ fontFamily: 'Georgia, serif' }}>{children}</h2>,
  h3: ({ children }) => <h3 id={headingToId(children)} className="text-lg font-bold text-gray-900 mt-8 mb-2 scroll-mt-28">{children}</h3>,
  p:  ({ children }) => <p className="text-gray-700 leading-relaxed mb-4">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-1.5 text-gray-700">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-1.5 text-gray-700">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a:  ({ href, children }) => (
    <a href={href} className="text-[#7B4FD8] underline underline-offset-2 hover:text-[#5e36b1]" target={href?.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{children}</a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#F5831F] pl-4 italic text-gray-600 my-6">{children}</blockquote>
  ),
  code: ({ inline, children }) =>
    inline
      ? <code className="px-1.5 py-0.5 rounded bg-gray-100 text-[13px] font-mono text-[#7B4FD8]">{children}</code>
      : <pre className="rounded-xl bg-gray-900 text-gray-100 text-[13px] font-mono p-4 overflow-x-auto my-4"><code>{children}</code></pre>,
  table: ({ children }) => (
    <div className="overflow-x-auto my-6">
      <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50 text-gray-600 text-left">{children}</thead>,
  th: ({ children }) => <th className="px-3 py-2 font-semibold border-b border-gray-200">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 border-t border-gray-100 align-top text-gray-700">{children}</td>,
  hr: () => <hr className="my-10 border-gray-200" />,
};

const DocsPage = () => {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDoc(null);
    setError(null);
    if (!slug) return;
    fetch(`${API_URL}/api/public/docs/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setDoc)
      .catch((e) => setError(String(e.message || e)));
  }, [slug]);

  const toc = useMemo(() => extractToc(doc?.markdown || ''), [doc]);

  // ── Index page (no slug) ────────────────────────────────────────────────
  if (!slug) {
    return <DocsIndex />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="docs-page">
      <PageMeta
        slug={`docs-${slug}`}
        path={`/docs/${slug}`}
        title={doc?.title ? `${doc.title} · HiBFF` : 'HiBFF Docs'}
        description={doc?.description || 'HiBFF long-form documentation.'}
      />

      <SiteNav mode="fixed" />

      <main className="pt-28 pb-16 px-4 max-w-6xl mx-auto">
        <Link
          to="/docs"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#7B4FD8] mb-6"
          data-testid="docs-back-to-index"
        >
          <ArrowLeft size={14} /> All documents
        </Link>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm" data-testid="docs-error">
            We couldn&apos;t load this document right now. ({error})
          </div>
        )}

        {!doc && !error && (
          <div className="text-gray-500 flex items-center gap-2 text-sm">
            <Loader2 className="animate-spin" size={14} /> Loading…
          </div>
        )}

        {doc && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-10">
            <article className="hibff-prose" data-testid="docs-content">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={MARKDOWN_COMPONENTS}
              >
                {doc.markdown}
              </ReactMarkdown>
            </article>

            <aside className="hidden lg:block">
              <div className="sticky top-28">
                <p className="text-[11px] uppercase tracking-widest font-bold text-gray-500 mb-3">On this page</p>
                <ul className="space-y-1.5 text-sm">
                  {toc.map((h) => (
                    <li key={h.id}>
                      <a href={`#${h.id}`} className="text-gray-600 hover:text-[#7B4FD8] block leading-snug">{h.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        )}
      </main>

      <DocsFooter />
    </div>
  );
};

// ── /docs index (lists every whitelisted public doc) ───────────────────────

const DocsIndex = () => {
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/public/docs`)
      .then((r) => r.json())
      .then((d) => setDocs(d.docs || []))
      .catch(() => setDocs([]));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="docs-index">
      <PageMeta
        slug="docs-index"
        path="/docs"
        title="HiBFF Documentation"
        description="The HiBFF long-form documentation: programme, charter, safeguarding, and partner playbooks."
      />
      <SiteNav mode="fixed" />
      <main className="pt-28 pb-16 px-4 max-w-4xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#F5831F' }}>HiBFF Documentation</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Georgia, serif' }}>
          Read what we&apos;ve built — in full.
        </h1>
        <p className="text-gray-700 leading-relaxed mb-10 max-w-2xl">
          The same long-form documents we publish on GitHub, rendered here for the people
          we ask to trust us — parents, teens, schools, and safeguarding leads.
        </p>

        {docs === null && <div className="text-gray-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Loading…</div>}
        {docs && docs.length === 0 && <p className="text-gray-500 text-sm">No documents available yet.</p>}
        <div className="space-y-4">
          {(docs || []).map((d) => (
            <Link
              key={d.slug}
              to={`/docs/${d.slug}`}
              className="block bg-white rounded-2xl p-6 border border-gray-200 hover:border-[#7B4FD8] hover:shadow-md transition-all"
              data-testid={`docs-index-card-${d.slug}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FFF5EA' }}>
                  <BookOpen size={18} style={{ color: '#F5831F' }} />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900 mb-1.5" style={{ fontFamily: 'Georgia, serif' }}>{d.title}</h2>
                  <p className="text-sm text-gray-600 leading-relaxed">{d.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <DocsFooter />
    </div>
  );
};

export default DocsPage;
